import { useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import * as creditCardServices from 'services/creditCardEula';

import { CustomNotification, dialogFunction } from 'common/components';
import { checkPassedRequirementProduct } from 'common/components/product-add/make-product-active/utils';
import { useRefetchAllMemberBillingStatement } from 'pages/company-profile/components/tabs/billing/queries';

export const validateActiveProductKey = (productIds) => {
  const mappingProductIds = productIds?.map((id) => parseInt(id)) ?? [];

  return ['credit-card', 'validate-active-product', ...mappingProductIds];
};

export const useValidateActiveProduct = ({
  productIds,
  ...options
}) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: validateActiveProductKey(productIds),
    queryFn: async () => {
      return await creditCardServices.validateActiveProduct({
        productIds,
      });
    },
    retry: 0,
    // staleTime: 0,
    ...options,
  });

  const handleRevalidateActiveProduct = () => {
    queryClient.invalidateQueries({
      queryKey: validateActiveProductKey(productIds),
    });
  };

  const refetchAndGetValidateProducts = async () => {
    const result = await query.refetch();

    return result?.data?.data?.products ?? [];
  };

  return {
    validateProducts: useMemo(
      () => query?.data?.data?.products ?? [],
      [query?.data]
    ),
    passedProducts:
      query?.data?.data?.products?.filter?.((product) =>
        checkPassedRequirementProduct(product)
      ) ?? [],
    handleRevalidateActiveProduct,
    refetchAndGetValidateProducts,
    ...query,
  };
};

export const mutationProductActiveKeys = ['payment', 'make-product-active'];

export const useMutateMakeProductActive = ({ onCancel, ...props } = {}) => {
  const refetchAllMemberBillingStatement =
    useRefetchAllMemberBillingStatement();

  return useMutation({
    mutationKey: mutationProductActiveKeys,
    mutationFn: creditCardServices.fetchProductActiveMaking,
    onSuccess: (response) => {
      if (response?.isSuccess) {
        CustomNotification.success('Make product active successfully');
      } else {
        const errorMsg = response?.message ?? 'Something went wrong';
        dialogFunction({
          type: 'warn',
          content: errorMsg,
          okText: 'OK',
          cancelButtonProps: { style: { display: 'none' } },
          onOk: () => {
            onCancel?.(); //* close charge dialog
          },
        });
      }
      refetchAllMemberBillingStatement();
    },
    onError: () => {
      CustomNotification.error('Failed to make product active');
    },
    ...props,
  });
};
